import React, { useState, useEffect } from 'react';
import { db } from '../../../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { FiEdit2, FiTrash2, FiPlus, FiSave, FiUpload, FiImage, FiArrowLeft } from 'react-icons/fi';
import { uploadToServer } from '../../../services/uploadService';

const PostsManager = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [currentPost, setCurrentPost] = useState({
    title: '', slug: '', excerpt: '', content: '', coverImage: '',
    categoryId: '', categoryName: '', authorId: '', authorName: '', authorAvatar: '',
    status: 'draft', metaTitle: '', metaDescription: '', tags: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [postsSnap, catSnap, authorsSnap] = await Promise.all([
        getDocs(query(collection(db, 'blog_posts'), orderBy('createdAt', 'desc'))),
        getDocs(collection(db, 'blog_categories')),
        getDocs(collection(db, 'blog_authors'))
      ]);
      
      setPosts(postsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setCategories(catSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setAuthors(authorsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const selectedCat = categories.find(c => c.id === currentPost.categoryId);
      const selectedAuthor = authors.find(a => a.id === currentPost.authorId);

      const postData = {
        ...currentPost,
        slug: currentPost.slug || currentPost.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, ''),
        categoryName: selectedCat ? selectedCat.name : '',
        authorName: selectedAuthor ? selectedAuthor.name : '',
        authorAvatar: selectedAuthor ? selectedAuthor.avatar : '',
      };

      if (currentPost.id) {
        await updateDoc(doc(db, 'blog_posts', currentPost.id), {
          ...postData,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'blog_posts'), {
          ...postData,
          createdAt: serverTimestamp()
        });
      }
      setIsEditing(false);
      fetchData();
    } catch (error) {
      console.error("Erro ao salvar post:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Tem certeza que deseja excluir este post?")) {
      try {
        await deleteDoc(doc(db, 'blog_posts', id));
        fetchData();
      } catch (error) {
        console.error("Erro ao deletar:", error);
      }
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const url = await uploadToServer(file, 'blog');
      setCurrentPost({ ...currentPost, coverImage: url });
    } catch (error) {
      console.error('Erro no upload', error);
      alert('Falha ao fazer upload da imagem.');
    } finally {
      setUploading(false);
    }
  };

  const getNewPostObj = () => ({
    title: '', slug: '', excerpt: '', content: '', coverImage: '',
    categoryId: '', categoryName: '', authorId: '', authorName: '', authorAvatar: '',
    status: 'draft', metaTitle: '', metaDescription: '', tags: []
  });

  if (loading) return <div className="p-8 text-center text-gray-500">Carregando posts...</div>;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 md:p-8">
      {!isEditing ? (
        <>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Gerenciar Posts</h2>
              <p className="text-gray-500">Crie, edite e publique artigos no blog.</p>
            </div>
            <button 
              onClick={() => { setCurrentPost(getNewPostObj()); setIsEditing(true); }} 
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <FiPlus /> Novo Post
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 text-sm">
                  <th className="pb-3 font-semibold">Título</th>
                  <th className="pb-3 font-semibold">Categoria</th>
                  <th className="pb-3 font-semibold">Status</th>
                  <th className="pb-3 font-semibold text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {posts.map(post => (
                  <tr key={post.id} className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="py-4">
                      <div className="flex items-center gap-3">
                        {post.coverImage ? (
                          <img src={post.coverImage} alt={post.title} className="w-12 h-8 object-cover rounded" />
                        ) : (
                          <div className="w-12 h-8 bg-gray-100 rounded flex items-center justify-center text-gray-400"><FiImage /></div>
                        )}
                        <span className="font-medium text-gray-900 dark:text-white line-clamp-1">{post.title}</span>
                      </div>
                    </td>
                    <td className="py-4 text-gray-500 dark:text-gray-400 text-sm">{post.categoryName || 'Sem categoria'}</td>
                    <td className="py-4">
                      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${post.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>
                        {post.status === 'published' ? 'Publicado' : 'Rascunho'}
                      </span>
                    </td>
                    <td className="py-4 flex justify-end gap-2">
                      <button onClick={() => { setCurrentPost(post); setIsEditing(true); }} className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700 rounded-lg"><FiEdit2 /></button>
                      <button onClick={() => handleDelete(post.id)} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-gray-700 rounded-lg"><FiTrash2 /></button>
                    </td>
                  </tr>
                ))}
                {posts.length === 0 && (
                  <tr><td colSpan="4" className="py-8 text-center text-gray-500">Nenhum post encontrado.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        /* Editor de Post */
        <div className="w-full">
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => setIsEditing(false)} className="flex items-center gap-2 text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors">
              <FiArrowLeft /> Voltar
            </button>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{currentPost.id ? 'Editar Post' : 'Criar Novo Post'}</h2>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              
              {/* Coluna Principal */}
              <div className="md:col-span-2 space-y-4">
                <div className="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Título do Post</label>
                  <input type="text" required value={currentPost.title} onChange={e => setCurrentPost({...currentPost, title: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white" />
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Resumo (Excerpt)</label>
                  <textarea rows="2" value={currentPost.excerpt} onChange={e => setCurrentPost({...currentPost, excerpt: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white" />
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Conteúdo do Post</label>
                    <div className="flex gap-2">
                      <button 
                        type="button" 
                        onClick={() => setCurrentPost({...currentPost, content: currentPost.content.replace(/\.\s+/g, '.\n\n')})}
                        className="text-xs bg-indigo-100 hover:bg-indigo-200 text-indigo-700 px-2 py-1 rounded transition-colors"
                        title="Quebra a linha e adiciona espaço após cada ponto final"
                      >
                        Auto-Formatar (Separar Parágrafos)
                      </button>
                      <button 
                        type="button" 
                        onClick={() => setCurrentPost({...currentPost, content: currentPost.content + ' <strong>Texto Negrito</strong>'})}
                        className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 rounded transition-colors"
                      >
                        + Negrito
                      </button>
                      <button 
                        type="button" 
                        onClick={() => setCurrentPost({...currentPost, content: currentPost.content + ' <h2>Novo Subtítulo</h2>'})}
                        className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 rounded transition-colors"
                      >
                        + Título
                      </button>
                    </div>
                  </div>
                  <textarea rows="15" required value={currentPost.content} onChange={e => setCurrentPost({...currentPost, content: e.target.value})} placeholder="Escreva seu texto aqui. O sistema respeitará as quebras de linha que você der (tecla Enter)." className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white font-mono text-sm leading-relaxed" />
                  <p className="text-xs text-gray-500 mt-2">Dica: As quebras de linha (Enter) que você usar aqui aparecerão exatamente assim no blog. Você pode usar o botão "Auto-Formatar" para criar parágrafos automaticamente após os pontos.</p>
                </div>
              </div>

              {/* Coluna Lateral */}
              <div className="space-y-4">
                
                <div className="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Imagem de Capa</label>
                  {currentPost.coverImage ? (
                    <div className="relative mb-3 group">
                      <img src={currentPost.coverImage} alt="Capa" className="w-full h-32 object-cover rounded-lg" />
                      <button type="button" onClick={() => setCurrentPost({...currentPost, coverImage: ''})} className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
                        <FiTrash2 />
                      </button>
                    </div>
                  ) : (
                    <div className="w-full h-32 bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center mb-3">
                      <FiImage className="text-gray-400 text-3xl" />
                    </div>
                  )}
                  <label className="flex items-center justify-center gap-2 px-4 py-2 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <FiUpload className="text-gray-500" />
                    <span className="text-sm font-medium">{uploading ? 'Enviando...' : 'Fazer Upload'}</span>
                    <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
                  </label>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                    <select value={currentPost.status} onChange={e => setCurrentPost({...currentPost, status: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white">
                      <option value="draft">Rascunho</option>
                      <option value="published">Publicado</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Categoria</label>
                    <select required value={currentPost.categoryId} onChange={e => setCurrentPost({...currentPost, categoryId: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white">
                      <option value="">Selecione...</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Autor</label>
                    <select required value={currentPost.authorId} onChange={e => setCurrentPost({...currentPost, authorId: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white">
                      <option value="">Selecione...</option>
                      {authors.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600 space-y-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm">SEO</h3>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Slug (URL)</label>
                    <input type="text" value={currentPost.slug} onChange={e => setCurrentPost({...currentPost, slug: e.target.value})} placeholder="gerado-automaticamente" className="w-full px-3 py-1.5 border rounded-lg text-sm outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Meta Title</label>
                    <input type="text" value={currentPost.metaTitle} onChange={e => setCurrentPost({...currentPost, metaTitle: e.target.value})} className="w-full px-3 py-1.5 border rounded-lg text-sm outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Meta Descrição</label>
                    <textarea rows="2" value={currentPost.metaDescription} onChange={e => setCurrentPost({...currentPost, metaDescription: e.target.value})} className="w-full px-3 py-1.5 border rounded-lg text-sm outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white" />
                  </div>
                </div>

              </div>
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 dark:border-gray-700">
              <button type="submit" disabled={uploading} className="flex items-center gap-2 px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl disabled:opacity-50 transition-all shadow-sm">
                <FiSave /> Salvar Post
              </button>
            </div>
          </form>

        </div>
      )}
    </div>
  );
};

export default PostsManager;
